import React, { useState } from 'react'
import Icon from './Icons'
import {
  PRESTATIONS,
  COMPLEXITES,
  VOLUMES,
  URGENCES,
  OPTIONS,
} from '../utils/pricingEngine'

// ─── Carte de sélection unique ────────────────────────────────────────────────

function OptionCard({ item, selected, onSelect, size = 'normal' }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.id)}
      className={`
        option-card text-left w-full
        ${selected ? 'selected' : ''}
        ${size === 'small' ? 'p-3' : 'p-4'}
      `}
      aria-pressed={selected}
    >
      {item.badge && (
        <span
          className={`
            badge mb-2
            ${selected
              ? 'bg-blue-100 text-blue-700'
              : 'bg-slate-100 text-slate-500'}
          `}
        >
          {item.badge}
        </span>
      )}
      {item.icon && (
        <div
          className={`
            w-9 h-9 rounded-lg flex items-center justify-center mb-3
            transition-colors duration-150
            ${selected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}
          `}
        >
          <Icon name={item.icon} className="w-5 h-5" />
        </div>
      )}
      <p
        className={`
          text-sm font-semibold leading-snug
          ${selected ? 'text-blue-700' : 'text-slate-800'}
        `}
      >
        {item.label}
      </p>
      {item.description && (
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          {item.description}
        </p>
      )}
    </button>
  )
}

// ─── Case à cocher option ─────────────────────────────────────────────────────

function CheckboxOption({ item, checked, onToggle }) {
  return (
    <label
      className={`
        flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer
        transition-all duration-150 select-none
        ${checked
          ? 'border-blue-500 bg-blue-50'
          : 'border-slate-200 bg-white hover:border-blue-200'}
      `}
    >
      <div className="flex-shrink-0 mt-0.5">
        <div
          className={`
            w-5 h-5 rounded border-2 flex items-center justify-center
            transition-all duration-150
            ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}
          `}
        >
          {checked && <Icon name="check" className="w-3 h-3 text-white" />}
        </div>
      </div>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={() => onToggle(item.id)}
      />
      <div>
        <p className={`text-sm font-semibold ${checked ? 'text-blue-700' : 'text-slate-800'}`}>
          {item.label}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
          {item.description}
        </p>
      </div>
    </label>
  )
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, required = true, children, error }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {required && (
          <span className="text-xs text-slate-400">(obligatoire)</span>
        )}
      </div>
      {children}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <Icon name="warning" className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
    </div>
  )
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function StepConfigurator({ config, onChange, onNext }) {
  const [errors, setErrors] = useState({})

  function handleSelect(field, value) {
    onChange({ ...config, [field]: value })
    if (errors[field]) {
      setErrors((e) => ({ ...e, [field]: null }))
    }
  }

  function toggleOption(optionId) {
    const current = config.options ?? []
    const next = current.includes(optionId)
      ? current.filter((o) => o !== optionId)
      : [...current, optionId]
    onChange({ ...config, options: next })
  }

  function handleSubmit() {
    const newErrors = {}
    if (!config.prestation)  newErrors.prestation  = 'Veuillez sélectionner un type de prestation.'
    if (!config.complexite)  newErrors.complexite  = 'Veuillez indiquer la complexité du projet.'
    if (!config.volume)      newErrors.volume      = 'Veuillez choisir le volume souhaité.'
    if (!config.urgence)     newErrors.urgence     = 'Veuillez indiquer le niveau d\'urgence.'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      // Scroll vers la première erreur
      const firstKey = Object.keys(newErrors)[0]
      document.getElementById(`section-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    onNext()
  }

  return (
    <div className="step-enter space-y-8">

      {/* Type de prestation */}
      <div id="section-prestation">
        <Section title="Type de prestation" error={errors.prestation}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {PRESTATIONS.map((item) => (
              <OptionCard
                key={item.id}
                item={item}
                selected={config.prestation === item.id}
                onSelect={(id) => handleSelect('prestation', id)}
              />
            ))}
          </div>
        </Section>
      </div>

      {/* Complexité */}
      <div id="section-complexite">
        <Section title="Complexité du projet" error={errors.complexite}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {COMPLEXITES.map((item) => (
              <OptionCard
                key={item.id}
                item={item}
                selected={config.complexite === item.id}
                onSelect={(id) => handleSelect('complexite', id)}
                size="small"
              />
            ))}
          </div>
        </Section>
      </div>

      {/* Volume */}
      <div id="section-volume">
        <Section title="Volume / Quantité" error={errors.volume}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {VOLUMES.map((item) => (
              <OptionCard
                key={item.id}
                item={item}
                selected={config.volume === item.id}
                onSelect={(id) => handleSelect('volume', id)}
                size="small"
              />
            ))}
          </div>
        </Section>
      </div>

      {/* Urgence */}
      <div id="section-urgence">
        <Section title="Délai souhaité" error={errors.urgence}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {URGENCES.map((item) => (
              <OptionCard
                key={item.id}
                item={item}
                selected={config.urgence === item.id}
                onSelect={(id) => handleSelect('urgence', id)}
                size="small"
              />
            ))}
          </div>
        </Section>
      </div>

      {/* Options supplémentaires */}
      <Section title="Options supplémentaires" required={false}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {OPTIONS.map((item) => (
            <CheckboxOption
              key={item.id}
              item={item}
              checked={(config.options ?? []).includes(item.id)}
              onToggle={toggleOption}
            />
          ))}
        </div>
      </Section>

      {/* CTA */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          className="btn-primary w-full sm:w-auto px-10 text-base py-4"
        >
          Estimer mon projet
          <Icon name="arrow" className="w-5 h-5" />
        </button>
        <p className="text-xs text-slate-400 mt-3">
          Une fourchette indicative vous sera communiquée après validation de votre adresse email.
        </p>
      </div>
    </div>
  )
}
